import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  isRouteErrorResponse,
  useLocation,
} from 'react-router';
import type { Route } from './+types/root';
import { NavProgress } from './components/NavProgress';
import { SearchBox } from './components/public/SearchBox';
import type { Card } from '#shared/types/public';

// Poppins — de LEZA-letter, zelf gehost (was een render-blokkerende
// Google @import). Vier gewichten dekken UI, koppen en nadruk.
import '@fontsource/poppins/400.css';
import '@fontsource/poppins/500.css';
import '@fontsource/poppins/600.css';
import '@fontsource/poppins/700.css';
// Cursief was er NIET, terwijl de artikelen 252 <em> en 106 blockquotes
// bevatten. De browser trok daarvoor de rechte letter scheef — een valse
// cursief die per platform anders uitpakt. 600-italic is nodig voor cursief
// binnen een kop en voor <strong><em>.
import '@fontsource/poppins/400-italic.css';
import '@fontsource/poppins/600-italic.css';
import './app.css';

// Anti-FOUC: stempelt ALTIJD expliciet light of dark (het oude script zette
// alleen dark en nooit light — met OS-voorkeur als default zou wie bewust
// licht koos op een donkere OS alsnog dark krijgen). Leest de nieuwe sleutel
// met terugval op de oude.
const THEME_BOOTSTRAP = `(function(){try{
var s=localStorage.getItem('kb:theme')||localStorage.getItem('kb-theme');
var d=s?s==='dark':matchMedia('(prefers-color-scheme: dark)').matches;
var r=document.documentElement;
r.setAttribute('data-theme',d?'dark':'light');
r.style.colorScheme=d?'dark':'light';
}catch(e){}})();`;

export function loader({ context }: Route.LoaderArgs) {
  return {
    user: context.user ?? null,
  };
}

export function Layout({ children }: { children: React.ReactNode }) {
  // Het manifest hoort ALLEEN bij de beheerschil: de PWA is er voor
  // medewerkers, en de publieke site moet een app die je niet kunt gebruiken
  // ook niet aanbieden. Op PAD gaten en niet op `user`, om twee redenen:
  // de ErrorBoundary draait zonder loaderdata (`useRouteLoaderData` geeft daar
  // undefined), en een geïnstalleerde app start op /admin, wordt door de guard
  // naar /admin/login gestuurd en zou daar zijn manifest kwijtraken — precies
  // het scherm waar een uitgelogde medewerker hem nodig heeft.
  const { pathname } = useLocation();
  const isBeheer = pathname === '/admin' || pathname.startsWith('/admin/');

  return (
    <html lang="nl">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <script dangerouslySetInnerHTML={{ __html: THEME_BOOTSTRAP }} />
        {/* Eén vaste merkkleur, BEWUST zonder media-query. Een tweede
            theme-color op (prefers-color-scheme: dark) zou de OS-voorkeur
            volgen, terwijl het thema van deze app uit localStorage komt (zie
            THEME_BOOTSTRAP hierboven) — precies wie bewust licht koos op een
            donkere OS zou dan een donkere browserbalk krijgen bij een lichte
            pagina. Dezelfde waarde staat in site.webmanifest. */}
        <meta name="theme-color" content="#F39000" />
        <link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png" />
        <link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png" />
        <link rel="icon" type="image/png" sizes="16x16" href="/favicon-16x16.png" />
        {/* Zodra er PNG-iconen staan gaan browsers /favicon.ico niet meer uit
            zichzelf ophalen. De ICO expliciet noemen houdt de 48px-variant
            beschikbaar voor een snelkoppeling op het bureaublad. Alle iconen
            komen uit assets/brand/kennisbank-icoon.jpg; zie npm run icons. */}
        <link rel="icon" type="image/x-icon" sizes="48x48" href="/favicon.ico" />
        {isBeheer && (
          <>
            <link rel="manifest" href="/site.webmanifest" />
            {/* iOS leest het manifest niet voor het opstartgedrag: zonder deze
                drie opent "Zet op beginscherm" gewoon Safari mét adresbalk. De
                titel is apart omdat iOS anders de <title> pakt van de pagina
                waarop je installeerde. `default` houdt de statusbalk buiten de
                pagina — met `black-translucent` schuift de inhoud eronder en is
                safe-area-opvang nodig, en die heeft de beheerschil niet. */}
            <meta name="mobile-web-app-capable" content="yes" />
            <meta name="apple-mobile-web-app-capable" content="yes" />
            <meta name="apple-mobile-web-app-status-bar-style" content="default" />
            <meta name="apple-mobile-web-app-title" content="Kennisbank" />
          </>
        )}
        <Meta />
        <Links />
      </head>
      <body className="min-h-screen">
        {/* Boven {children}: de balk hoort bij de schil, niet bij de pagina die
            hij aankondigt. */}
        <NavProgress />
        {children}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export default function App() {
  return <Outlet />;
}

export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
  let title = 'Er ging iets mis';
  let detail = 'Een onverwachte fout. Probeer het opnieuw of ga terug naar de kennisbank.';
  let is404 = false;
  // De loaders van /artikel/:slug en de catch-all geven bij een 404 artikelen
  // mee die lijken op wat er in de URL stond (notFoundSuggestions). Zo is een
  // hernoemd of verhuisd artikel één klik weg in plaats van een dood spoor.
  let suggesties: Card[] = [];

  if (isRouteErrorResponse(error)) {
    is404 = error.status === 404;
    title = is404 ? 'Pagina niet gevonden' : `Fout ${error.status}`;
    detail = is404
      ? 'Deze pagina bestaat niet (meer). Zoek in de kennisbank of kies hieronder een artikel.'
      : error.statusText || detail;
    const d = error.data as { suggesties?: Card[] } | null;
    if (is404 && d && Array.isArray(d.suggesties)) suggesties = d.suggesties;
  } else if (import.meta.env.DEV && error instanceof Error) {
    detail = error.message;
  }

  // Op het beheer geen zoekveld: dat zoekt in de publieke kennisbank, en wie
  // hier op een 404 landt zoekt een beheerscherm.
  const isBeheer = useLocation().pathname.startsWith('/admin');

  // kb-public: de foutpagina's lopen niet via PublicLayout en zouden anders
  // als enige publieke scherm de compacte beheerschaal houden.
  return (
    <main className="kb-public mx-auto flex min-h-[60vh] max-w-xl flex-col items-center justify-center gap-4 px-6 py-12 text-center">
      {/* Een route die gooit draait zijn eigen meta() niet meer, dus zonder
          dit blijft de foutpagina titelloos — voor wie op tabbladtitels
          navigeert is dat het enige houvast. React 19 hijst <title> zelf
          naar de <head>. */}
      <title>{`${title} — LEZA Kennisbank`}</title>
      <meta name="robots" content="noindex" />
      <p className="font-display text-6xl font-semibold text-accent" aria-hidden>×</p>
      <h1 className="font-display text-3xl font-semibold text-ink">{title}</h1>
      <p className="text-ink-2">{detail}</p>

      {is404 && !isBeheer && (
        <div className="mt-2 w-full max-w-lg">
          <SearchBox size="md" />
        </div>
      )}

      {suggesties.length > 0 && (
        <section className="mt-2 w-full max-w-lg text-left" aria-labelledby="misschien-zocht-je">
          <h2 id="misschien-zocht-je" className="mb-2 text-2xs font-semibold tracking-wider text-ink-3 uppercase">
            Misschien zocht je
          </h2>
          <ul className="divide-y divide-hairline rounded-lg border border-hairline bg-surface shadow-sm">
            {suggesties.map((s) => (
              <li key={s.id}>
                <a href={`/artikel/${encodeURIComponent(s.slug || s.id)}`} className="block px-4 py-2.5 transition hover:bg-surface-hover">
                  <span className="block text-base font-medium text-ink">{s.name}</span>
                  {s.excerpt && <span className="mt-0.5 line-clamp-1 block text-sm text-ink-3">{s.excerpt}</span>}
                </a>
              </li>
            ))}
          </ul>
        </section>
      )}

      <a
        href={isBeheer ? '/admin' : '/'}
        className="mt-2 rounded-md bg-accent px-5 py-2.5 text-base font-semibold text-accent-on shadow-sm transition hover:bg-accent-hover hover:shadow-glow"
      >
        {isBeheer ? 'Terug naar het beheer' : 'Terug naar de kennisbank'}
      </a>
    </main>
  );
}
